import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Uploads a large file to the Object Store in small chunks (bypasses the ~100MB
// ingress limit). Returns { path, content_type, filename }. onProgress -> 0..100.
export async function uploadFileInChunks(file, kind, onProgress) {
  const CHUNK = 8 * 1024 * 1024; // 8 MB per request
  const { data: init } = await api.post("/admin/uploads/init");
  const uploadId = init.upload_id;
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK));
    await api.post(`/admin/uploads/${uploadId}/chunk?index=${i}`, blob, {
      headers: { "Content-Type": "application/octet-stream" },
    });
    if (onProgress) onProgress(Math.round(((i + 1) / total) * 100));
  }
  const { data } = await api.post(`/admin/uploads/${uploadId}/complete`, {
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    kind,
  });
  return data;
}

// Compress an artwork/image file in the browser before upload (keeps audio
// untouched). Scales the longest edge down to `maxDim` and re-encodes as JPEG.
// Returns a File; falls back to the original if anything fails or it grows.
export async function compressImage(file, { maxDim = 1600, quality = 0.82 } = {}) {
  if (!file || !file.type?.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    const img = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = rej;
      im.src = dataUrl;
    });
    let { width, height } = img;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file; // no win — keep original
    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

const RESUMABLE_THRESHOLD = 16 * 1024 * 1024; // 16 MB — switch to resumable sessions
const RESUMABLE_CHUNK = 8 * 1024 * 1024; // 8 MB (multiple of 256 KB, required by GCS)

function putResumableChunk(sessionUrl, blob, start, total, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", sessionUrl);
    xhr.withCredentials = false;
    const end = start + blob.size - 1;
    xhr.setRequestHeader("Content-Range", `bytes ${start}-${end}/${total}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(start + e.loaded, total);
    };
    xhr.onload = () => {
      // 308 = resume incomplete (more chunks), 200/201 = finished
      if (xhr.status === 308 || xhr.status === 200 || xhr.status === 201) resolve(xhr.status);
      else reject(new Error(`Chunk upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.send(blob);
  });
}

async function uploadResumable(file, sign, onProgress) {
  // 1) Initiate the resumable session and read the session URI from Location.
  const initRes = await fetch(sign.url, {
    method: "POST",
    headers: sign.headers || {},
    body: "",
  });
  if (!initRes.ok) throw new Error(`Could not start upload (${initRes.status})`);
  const sessionUrl = initRes.headers.get("Location") || initRes.headers.get("location");
  if (!sessionUrl) throw new Error("Upload session URL missing");
  // 2) PUT the file in chunks, retrying each a few times.
  const total = file.size;
  let start = 0;
  while (start < total) {
    const chunk = file.slice(start, Math.min(total, start + RESUMABLE_CHUNK));
    let attempt = 0;
    for (;;) {
      try {
        await putResumableChunk(sessionUrl, chunk, start, total,
          (loaded, tot) => onProgress && onProgress(Math.round((loaded * 100) / tot)));
        break;
      } catch (e) {
        if (++attempt >= 3) throw e;
        await new Promise((r) => setTimeout(r, 1000 * attempt));
      }
    }
    start += chunk.size;
    if (onProgress) onProgress(Math.round((start * 100) / total));
  }
}

// Uploads a file directly to Google Cloud Storage. Large files use a resumable
// session (chunked + retried); smaller files use a single signed PUT. Bytes
// never pass through the backend. Returns { path, content_type, filename }.
export async function uploadFileToStorage(file, kind, onProgress) {
  const resumable = file.size > RESUMABLE_THRESHOLD;
  const { data: sign } = await api.post("/admin/uploads/sign", {
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    kind,
    resumable,
  });
  if (sign.resumable) {
    await uploadResumable(file, sign, onProgress);
  } else {
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", sign.url);
      // Do NOT send credentials to GCS; the signed URL is the auth.
      xhr.withCredentials = false;
      Object.entries(sign.headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded * 100) / e.total));
      };
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`Upload failed (${xhr.status})`));
      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.send(file);
    });
  }
  return { path: sign.path, content_type: sign.content_type, filename: sign.filename };
}

export default api;
